import useProductStore from '@/store/product'
import useSettingStore from '@/store/setting'
import useCustomizationStore from '@/store/customization'
import useAppendComponent from '@/composables/appendComponent'
import { useRoute } from 'vue-router'
import { appendChildElementIntoParentElement } from '@/utils/dom'
import { extractProductSKUFromHandle, isProductHasTags } from '@/utils/product'
import { computed, getCurrentInstance, nextTick, ref } from 'vue'
import {
  CUSTOMILY_FILE_INPUT_WRAPPER_CLASS,
  CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR,
  INPUT_FILE_CUSTOMILY_SELECTOR,
  QUERY_STRING_NO_CACHE,
  REMOVE_BUTTON_CUSTOMILY_ATTRIBUTE,
  REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR,
  SELECT_IMAGE_BUTTON_CUSTOMILY_SELECTOR,
  SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX,
  SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX,
  INPUT_CALENDAR_CUSTOMILY_SELECTOR,
  HELP_TEXT_CUSTOMILY_SELECTOR,
  CL_CONTAINER_CUSTOMILY_SELECTOR,
  PRODUCT_TAG_CUSTOM_CROSS_WORDS,
  PRODUCT_TAG_CUSTOM_PHOTO_WORD,
} from '@/utils'
import CustomUploadImageStyleTransfer from '../CustomUploadImageStyleTransfer.vue'
import CustomCalendar from '../CustomCalendar.vue'
import CustomUploadImageFaceSwap from '../CustomUploadImageFaceSwap.vue'
import CustomUploadQrCodeSoundWave from '../CustomUploadQrCodeSoundWave.vue'
import CustomCrossWords from '../CustomCrossWords.vue'
import CustomPhotoWord from '../CustomPhotoWord.vue'
import Logger from '@/services/log'

export const useProductCustomization = () => {
  const productStore = useProductStore()
  const settingStore = useSettingStore()
  const route = useRoute()
  const customizationStore = useCustomizationStore()
  const { appContext } = getCurrentInstance()
  const { renderComponent } = useAppendComponent()

  const percentProcessed = ref(0)
  let interval: any = null
  const STYLE_TRANSFER_OPTION_TYPE = 'style_transfer'
  const FACE_SWAP_OPTION_TYPE = 'face_swap'
  const QRCODE_SOUND_OPTION_TYPE = 'qrcode_sound'
  const CALENDAR_OPTION_TYPE = 'calendar'
  const CROSS_WORDS_OPTION_TYPE = 'cross_words'
  const PHOTO_WORD_OPTION_TYPE = 'photo_word'

  const isCustomStyleTransfer = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productStyleTransferTag || '',
    ])
  )

  const isCustomFaceSwap = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productFaceSwapTag || '',
    ])
  )

  const isCustomQrCodeSoundWave = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productQrCodeSoundWave || '',
    ])
  )

  const isCustomCalendar = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productTagCalendar || '',
    ])
  )

  const isCustomCrossWords = computed(() =>
    isProductHasTags(productStore.product, [
      PRODUCT_TAG_CUSTOM_CROSS_WORDS || '',
    ])
  )

  const isCustomPhotoWord = computed(() =>
    isProductHasTags(productStore.product, [
      PRODUCT_TAG_CUSTOM_PHOTO_WORD || '',
    ])
  )

  const isCustomUpload = computed(
    () =>
      isCustomStyleTransfer.value ||
      isCustomFaceSwap.value ||
      isCustomQrCodeSoundWave.value ||
      isCustomCalendar.value ||
      isCustomCrossWords.value ||
      isCustomPhotoWord.value
  )

  async function fetchCustomizationTemplates() {
    if (!productStore.product || !isCustomUpload.value) return
    const payload = {
      code: extractProductSKUFromHandle(productStore.product.handle),
      type: productStore.product.productType || '',
      tag: productStore.product.tags?.join('|') || '',
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    }
    await customizationStore.fetchCustomizationTemplates(payload)
  }

  async function appendCustomUploadImage() {
    if (
      !customizationStore.customizationTemplates?.length ||
      !isCustomUpload.value
    )
      return

    await nextTick()

    const numberCustomTag = getNunberCustomTag()

    customizationStore.customizationTemplates.forEach(
      async (customizationTemplate, index) => {
        const customilyOptionWrapper = document.querySelector(
          `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
            'option_sort_id',
            customizationTemplate.option_sort_id
          )}`
        )
        if (!customilyOptionWrapper) return

        if (
          (numberCustomTag <= 1 &&
            !isCustomStyleTransfer.value &&
            !isCustomPhotoWord.value) ||
          (numberCustomTag > 1 &&
            customizationTemplate.option_type != STYLE_TRANSFER_OPTION_TYPE &&
            customizationTemplate.option_type != PHOTO_WORD_OPTION_TYPE)
        ) {
          const lableOption = customilyOptionWrapper.querySelector(`label`)
          if (lableOption) lableOption.style.display = 'none'
        }

        const customilyElementWrapper = customilyOptionWrapper.querySelector(
          `.${
            isCustomCalendar.value
              ? 'cl-option-content'
              : CUSTOMILY_FILE_INPUT_WRAPPER_CLASS
          }`
        )

        if (!customilyElementWrapper) return

        // if customization component appended, we do not anything
        const isAppendedUploadImage = customilyElementWrapper.querySelector(
          `.${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
        )
        if (isAppendedUploadImage) return

        // hide input calendar customily
        const inputCalendarCustomily: HTMLElement | null =
          customilyElementWrapper.querySelector(
            INPUT_CALENDAR_CUSTOMILY_SELECTOR
          )
        if (inputCalendarCustomily) {
          inputCalendarCustomily.style.display = 'none'
        }

        // hide select image button and remove image button customily
        const selectImageButtonCustomily: HTMLElement | null =
          customilyElementWrapper.querySelector(
            SELECT_IMAGE_BUTTON_CUSTOMILY_SELECTOR
          )
        if (selectImageButtonCustomily) {
          selectImageButtonCustomily.style.display = 'none'
        }
        const removeImageButtonCustomily =
          customilyElementWrapper.querySelector(
            REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR
          )
        customilyElementWrapper.lastElementChild!.setAttribute(
          REMOVE_BUTTON_CUSTOMILY_ATTRIBUTE,
          'true'
        )
        const inputFileCustomily = customilyElementWrapper.querySelector(
          INPUT_FILE_CUSTOMILY_SELECTOR
        )

        // hide close button customily and help text upload image for custom cross word product or photo word product
        if (isCustomCrossWords.value || isCustomPhotoWord.value) {
          const helpText = customilyOptionWrapper.querySelector(
            HELP_TEXT_CUSTOMILY_SELECTOR
          )
          if (helpText) helpText.style.display = 'none'

          const clContainerCustomily = customilyOptionWrapper.querySelector(
            `.${CL_CONTAINER_CUSTOMILY_SELECTOR}`
          )
          if (clContainerCustomily) clContainerCustomily.style.display = 'none'
        }

        // create element and append to first index upload section customily
        const customeElement = document.createElement('div')
        customeElement.setAttribute(
          'id',
          `${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-${index}`
        )
        customeElement.setAttribute(
          'class',
          `${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
        )
        appendChildElementIntoParentElement(
          customilyElementWrapper,
          customeElement,
          0
        )
        await nextTick()

        // append component into DOM
        const element = document.querySelector(
          `#${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-${index}`
        )
        if (!element) return

        const renderComponentStyleTransfer = () =>
          renderComponent({
            element,
            component: CustomUploadImageStyleTransfer,
            props: {
              id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
              inputFileCustomily,
              selectImageButtonCustomily,
              removeImageButtonCustomily,
              customizationTemplate,
              optionSortId: customizationTemplate.option_sort_id,
            },
            appContext,
          })

        const renderComponentFaceSwap = () =>
          renderComponent({
            element,
            component: CustomUploadImageFaceSwap,
            props: {
              id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
              inputFileCustomily,
              selectImageButtonCustomily,
              optionSortId: customizationTemplate.option_sort_id,
              templates: customizationTemplate.templates,
            },
            appContext,
          })

        const renderComponentQrCodeSoundWave = () =>
          renderComponent({
            element,
            component: CustomUploadQrCodeSoundWave,
            props: {
              id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
              customizationTemplate:
                customizationTemplate.templates[0].extended_settings,
            },
            appContext,
          })

        const renderComponentCalander = () =>
          renderComponent({
            element,
            component: CustomCalendar,
            props: {
              dayIds: customizationTemplate.templates[0].day_ids,
              monthId: customizationTemplate.templates[0].month_id,
              yearId: customizationTemplate.templates[0].year_id,
            },
            appContext,
          })

        const renderComponentCrossWords = () =>
          renderComponent({
            element,
            component: CustomCrossWords,
            props: {
              inputFileCustomily,
              customizationTemplate: customizationTemplate.templates[0],
              customilyOptionWrapper,
            },
            appContext,
          })

        const renderComponentPhotoWord = () =>
          renderComponent({
            element,
            component: CustomPhotoWord,
            props: {
              inputFileCustomily,
              customizationTemplate: customizationTemplate.templates[0],
              customilyOptionWrapper,
              optionSortId: customizationTemplate.option_sort_id,
            },
            appContext,
          })

        if (numberCustomTag > 1) {
          if (
            isCustomStyleTransfer.value &&
            customizationTemplate.option_type == STYLE_TRANSFER_OPTION_TYPE
          ) {
            renderComponentStyleTransfer()
          }
          if (
            isCustomFaceSwap.value &&
            customizationTemplate.option_type == FACE_SWAP_OPTION_TYPE
          ) {
            renderComponentFaceSwap()
          }
          if (
            isCustomQrCodeSoundWave.value &&
            customizationTemplate.option_type == QRCODE_SOUND_OPTION_TYPE
          ) {
            renderComponentQrCodeSoundWave()
          }
          if (
            isCustomCalendar.value &&
            customizationTemplate.option_type == CALENDAR_OPTION_TYPE
          ) {
            renderComponentCalander()
          }

          if (
            isCustomCrossWords.value &&
            customizationTemplate.option_type == CROSS_WORDS_OPTION_TYPE
          ) {
            renderComponentCrossWords()
          }

          if (
            isCustomPhotoWord.value &&
            customizationTemplate.option_type == PHOTO_WORD_OPTION_TYPE
          ) {
            renderComponentPhotoWord()
          }
        } else {
          if (isCustomStyleTransfer.value) {
            renderComponentStyleTransfer()
          } else if (isCustomFaceSwap.value) {
            renderComponentFaceSwap()
          } else if (isCustomQrCodeSoundWave.value) {
            renderComponentQrCodeSoundWave()
          } else if (isCustomCalendar.value) {
            renderComponentCalander()
          } else if (isCustomCrossWords.value) {
            renderComponentCrossWords()
          } else if (isCustomPhotoWord.value) {
            renderComponentPhotoWord()
          }
        }
      }
    )
  }

  async function convertImageLinkToFile(imageLink: string, fileName: string) {
    try {
      const response = await fetch(imageLink)
      const blob = await response.blob()
      const file = new File([blob], fileName, { type: 'image/png' })
      return file
    } catch (err) {
      Logger.log('Error converting URL image to input file:', err)
    }
  }

  async function convertImageLinkToFileList(
    imageLink: string,
    fileName: string
  ) {
    try {
      const file = await convertImageLinkToFile(imageLink, fileName)
      const files = new DataTransfer()
      files.items.add(file!)
      return files.files
    } catch (err) {
      Logger.log('Error converting URL image to input file list:', err)
    }
  }

  function changeFileImageCustomily(files?: FileList, inputElement?: any) {
    if (!files || !inputElement) return
    inputElement.files = files
    const event = new Event('change')
    inputElement.dispatchEvent(event)
  }

  function changeInputCustomily(value: string, inputElement?: any) {
    if (!inputElement) return
    inputElement.value = value
    const event = new Event('input')
    inputElement.dispatchEvent(event)
  }

  function changeOptionSelectCustomily(value: string, inputElement?: any) {
    if (!inputElement) return
    inputElement.value = value
    const event = new Event('change')
    inputElement.dispatchEvent(event)
  }

  function startUpload() {
    if (interval) {
      clearTimeout(interval)
    }
    percentProcessed.value = 0
    const oneSecondInMiliseconds = 1000
    interval = setInterval(run, oneSecondInMiliseconds)
    const times = [5, 10, 15, 20, 25, 30, 35, 40] // list time in second
    const percents = [10, 20, 30, 40, 50, 60, 70, 80]
    let timeProcessing = 0

    function run() {
      if (timeProcessing > times.slice(-1)[0]) return
      timeProcessing += 1
      if (times.includes(timeProcessing)) {
        percentProcessed.value =
          percents.length > 1 ? percents.shift()! : percents.slice(-1)[0]
      }
    }
  }
  function finishUpload() {
    if (interval) {
      clearTimeout(interval)
    }
  }

  function getNunberCustomTag() {
    let numberCustomTag = 0
    numberCustomTag += isCustomStyleTransfer.value ? 1 : 0
    numberCustomTag += isCustomFaceSwap.value ? 1 : 0
    numberCustomTag += isCustomQrCodeSoundWave.value ? 1 : 0
    numberCustomTag += isCustomCalendar.value ? 1 : 0
    return numberCustomTag
  }

  return {
    fetchCustomizationTemplates,
    appendCustomUploadImage,
    convertImageLinkToFile,
    convertImageLinkToFileList,
    changeFileImageCustomily,
    changeInputCustomily,
    startUpload,
    finishUpload,
    percentProcessed,
    changeOptionSelectCustomily,
  }
}
